import { z } from 'zod';

// Paginación por cursor en todos los listados (RF-07.04). El cursor es opaco para el
// cliente: base64url de un JSON que solo interpreta el servidor, que además lo valida
// con el esquema del listado. Nunca se pagina por desplazamiento (`offset`).

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
const MAX_CURSOR_LENGTH = 512;

export const cursorPageQuerySchema = z.object({
  cursor: z.string().min(1).max(MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export type CursorPageQuery = z.infer<typeof cursorPageQuerySchema>;

/** Respuesta de un listado: `nextCursor` es `null` en la última página. */
export function cursorPageSchema<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export class InvalidCursorError extends Error {
  constructor() {
    super('Cursor no válido');
    this.name = 'InvalidCursorError';
  }
}

// Sin `Buffer`: el paquete también se usa en el navegador.
function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

export function encodeCursor(position: unknown): string {
  return toBase64Url(JSON.stringify(position));
}

/** Lanza `InvalidCursorError` si el cursor no es base64url de un JSON con la forma esperada. */
export function decodeCursor<T extends z.ZodType>(cursor: string, schema: T): z.infer<T> {
  if (cursor.length > MAX_CURSOR_LENGTH || !/^[A-Za-z0-9_-]+$/.test(cursor)) {
    throw new InvalidCursorError();
  }
  let raw: unknown;
  try {
    raw = JSON.parse(fromBase64Url(cursor));
  } catch {
    throw new InvalidCursorError();
  }
  const parsed = schema.safeParse(raw);
  if (!parsed.success) throw new InvalidCursorError();
  return parsed.data;
}

/**
 * Construye una página a partir de `limit + 1` filas ya ordenadas: si sobra una, hay
 * página siguiente y su cursor apunta a la última fila devuelta.
 */
export function toCursorPage<Row, Item>(
  rows: readonly Row[],
  limit: number,
  position: (row: Row) => unknown,
  toItem: (row: Row) => Item,
): CursorPage<Item> {
  const pageRows = rows.slice(0, limit);
  const last = pageRows.at(-1);
  const hasMore = rows.length > limit && last !== undefined;
  return {
    items: pageRows.map(toItem),
    nextCursor: hasMore ? encodeCursor(position(last)) : null,
  };
}
